import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon
} from '@mui/icons-material';

/**
 * ResumeContent - Grouped and formatted resume content
 * 
 * Groups items by section (Experience, Education, Projects, Skills)
 * and renders them as professional resume text.
 */
const ResumeContent = ({ profile, profileData: profileDataProp, selectedItems, resumeConfig }) => {
  // Use annotated profileData (has _display flags)
  const profileData = profileDataProp || profile;
  
  if (!profileData) {
    return null;
  }

  // Render a section heading
  const SectionHeading = ({ children }) => (
    <Box sx={{ mb: 2, mt: 3 }}>
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 700,
          fontSize: '0.95rem',
          letterSpacing: '0.5px',
          color: 'text.primary',
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          pb: 0.5,
          mb: 1.5
        }}
      >
        {children}
      </Typography>
    </Box>
  );

  // Render experience entry (now with grouped accomplishments)
  const ExperienceEntry = ({ item }) => {
    // Data is already extracted by grouping logic
    const { title, company, location, duration, accomplishments } = item;

    return (
      <Box sx={{ mb: 2.5 }}>
        {/* Job Title */}
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
          {title}
        </Typography>
        
        {/* Company | Location | Dates */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {company}
          {location ? ` | ${location}` : ''}
          {duration ? ` | ${duration}` : ''}
        </Typography>
        
        {/* Accomplishments/Bullets */}
        {accomplishments && accomplishments.length > 0 && (
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
            {accomplishments.map((acc, idx) => (
              <Typography 
                key={idx} 
                component="li" 
                variant="body2" 
                sx={{ mb: 0.5, lineHeight: 1.6 }}
              >
                {typeof acc === 'string' ? acc : (acc.text || acc.description || JSON.stringify(acc))}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Render education entry
  const EducationEntry = ({ item }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {item.degree || item.content?.degree || 'Degree'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {item.institution || item.content?.institution || 'Institution'}
        {item.graduation_date || item.content?.graduation_date ? ` | ${item.graduation_date || item.content?.graduation_date}` : ''}
      </Typography>
      {item.gpa && (
        <Typography variant="body2" color="text.secondary">
          GPA: {item.gpa}
        </Typography>
      )}
    </Box>
  );

  // Render project entry
  const ProjectEntry = ({ item }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
        {item.name || item.title || 'Project'}
      </Typography>
      {item.duration && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {item.duration}
        </Typography>
      )}
      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
        {item.description}
      </Typography>
    </Box>
  );

  return (
    <Box className="resume-printable">
      {/* PAGE 1 */}
      <Paper 
        className="resume-page"
        sx={{ 
          width: '8.5in',
          minHeight: '11in',
          p: '0.75in',
          bgcolor: 'white',
          mb: 3,
          boxShadow: 3,
          '@media print': {
            boxShadow: 'none',
            mb: 0,
            pageBreakAfter: 'always'
          }
        }}>
        {/* Personal Header */}
        <Box sx={{ mb: 2, textAlign: 'center', borderBottom: '2px solid', borderColor: 'primary.main', pb: 1.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.75, fontSize: '1.5rem' }}>
            {profileData.personal?.name || 'Your Name'}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ fontSize: '0.8rem' }}>
            {profileData.personal?.email && (
              <Typography variant="body2" color="text.secondary">
                {profileData.personal.email}
              </Typography>
            )}
            {profileData.personal?.phone && (
              <Typography variant="body2" color="text.secondary">
                {profileData.personal.phone}
              </Typography>
            )}
            {profileData.personal?.location && (
              <Typography variant="body2" color="text.secondary">
                {profileData.personal.location}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Experience Section - Page 1 */}
        {profileData.experience && profileData.experience.length > 0 && (
          <Box>
            <SectionHeading>EXPERIENCE</SectionHeading>
            {profileData.experience.map((exp, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.25 }}>
                  {exp.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                  {exp.company}
                  {exp.location ? ` | ${exp.location}` : ''}
                  {(exp.start_date || exp.end_date) ? ` | ${exp.start_date || ''} - ${exp.end_date || 'Present'}` : ''}
                </Typography>
                {/* Selected accomplishments */}
                {exp.accomplishments && exp.accomplishments.filter(acc => acc._display).length > 0 && (
                  <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                    {exp.accomplishments.filter(acc => acc._display).map((acc, accIdx) => (
                      <Typography 
                        key={accIdx}
                        component="li" 
                        variant="body2" 
                        sx={{ fontSize: '0.85rem', mb: 0.4, lineHeight: 1.5 }}
                      >
                        {typeof acc === 'string' ? acc : (acc.text || acc.description || '')}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* PAGE 2 */}
      <Paper 
        className="resume-page"
        sx={{ 
          width: '8.5in',
          minHeight: '11in',
          p: '0.75in',
          bgcolor: 'white',
          boxShadow: 3,
          '@media print': {
            boxShadow: 'none'
          }
        }}>
        {/* Education Section */}
        {profileData.education && profileData.education.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <SectionHeading>EDUCATION</SectionHeading>
            {profileData.education.map((edu, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {edu.degree || 'Degree'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  {edu.institution || 'Institution'}
                  {edu.graduation_date ? ` | ${edu.graduation_date}` : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Skills Section */}
        {profileData.skills && (profileData.skills.technical?.length > 0 || profileData.skills.tools?.length > 0) && (
          <Box>
            <SectionHeading>SKILLS</SectionHeading>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {profileData.skills?.technical?.map((skill, index) => (
                <Chip key={`tech-${index}`} label={skill} size="small" sx={{ fontSize: '0.75rem' }} />
              ))}
              {profileData.skills?.tools?.map((tool, index) => (
                <Chip key={`tool-${index}`} label={tool} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/**
 * ResumePreviewPanel - Center panel showing live resume preview
 * 
 * Shows professional formatted resume with grouped sections.
 */
/**
 * Annotate profile with display flags based on selected IDs
 * Each item gets _display: true/false indicating if it should render
 */
const annotateProfileWithDisplay = (profile, selectedIds) => {
  if (!profile) return null;
  
  const isSelected = (id) => selectedIds.includes(id);
  
  return {
    personal: profile.personal || {}, // Always visible
    
    summaries: profile.summaries || [], // Always visible if present
    
    experience: (profile.experience || []).map(exp => {
      const annotatedAccomplishments = (exp.accomplishments || []).map((acc, idx) => {
        // Generate ID same way as ProfileTab does - use existing ID or generate
        const accText = typeof acc === 'string' ? acc : (acc.text || acc.description || '');
        const accId = acc.id || `experience-${exp.company}-${accText}`;
        return {
          ...acc,
          _display: isSelected(accId)
        };
      });
      
      return {
        ...exp,
        accomplishments: annotatedAccomplishments,
        _display: annotatedAccomplishments.some(a => a._display) // Show position if any acc visible
      };
    }),
    
    highlights: (profile.highlights || []).map(highlight => {
      const annotatedItems = (highlight.items || []).map((item, idx) => {
        const itemId = item.id || `highlight-${highlight.category}-${item.text || item}`;
        return {
          ...item,
          _display: isSelected(itemId)
        };
      });
      
      return {
        ...highlight,
        items: annotatedItems,
        _display: annotatedItems.some(i => i._display) // Show category if any item visible
      };
    }),
    
    projects: (profile.projects || []).map(project => {
      const projectId = project.id || `project-${project.name}`;
      return {
        ...project,
        _display: isSelected(projectId)
      };
    }),
    
    education: (profile.education || []).map(edu => {
      const eduId = edu.id || `education-${edu.institution}-${edu.degree}`;
      return {
        ...edu,
        _display: isSelected(eduId)
      };
    }),
    
    skills: profile.skills || {} // Always visible
  };
};

const ResumePreviewPanel = ({ profile, selectedItems, resumeConfig, application, graphData }) => {
  // Get selected IDs from graphData
  const selectedIds = graphData?.getSelectedNodes()?.map(n => n.item_id) || [];

  // Annotate profile with display flags
  const profileData = profile ? annotateProfileWithDisplay(profile, selectedIds) : null;

  if (!profileData && !graphData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Loading profile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Resume Pages - Rendered by ResumeContent */}
      <ResumeContent 
        profile={profile}
        profileData={profileData}
        selectedItems={selectedItems}
        resumeConfig={resumeConfig}
      />

      {/* Page Count Indicator */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Target: {resumeConfig?.targetPages || 2} pages • 
          Template: {resumeConfig?.template || 'modern'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ResumePreviewPanel;
